/**
 * Reglas de la configuración de la clínica: RUC, roles y lo que puede hacer cada uno, colores de marca,
 * validación de usuarios y numeración de documentos.
 */
import { norm } from "./texto.ts";
import { celularValido, correoValido } from "./identificacion.ts";

export const ROLES = ["admin", "odontologo", "especialista", "asistente", "recepcion", "director", "tutor", "estudiante", "laboratorio"] as const;
/** Roles que solo se ofrecen en una clínica universitaria. */
export const ROLES_ACADEMICOS: readonly string[] = ["director", "tutor", "estudiante"];
export type RolUsuario = (typeof ROLES)[number];

/** Lo que ve el administrador al elegir el rol de un usuario. */
export const DESCRIPCION_ROL: Readonly<Record<RolUsuario, { nombre: string; puede: string; noPuede: string }>> = {
  admin: { nombre: "Administrador", puede: "Todo: pacientes, historia clínica, caja, agenda, mensajes, usuarios, configuración y auditoría.", noPuede: "—" },
  odontologo: { nombre: "Odontólogo/a", puede: "Pacientes, historia clínica, odontograma, firmar notas, recetas, consentimientos y certificados; cobrar y agendar.", noPuede: "Anular abonos, configurar la clínica ni manejar usuarios." },
  especialista: { nombre: "Especialista", puede: "Lo mismo que un odontólogo, incluidos los módulos de especialidad.", noPuede: "Anular abonos, configurar la clínica ni manejar usuarios." },
  asistente: { nombre: "Asistente dental", puede: "Registrar y editar la ficha de los pacientes, ver la historia clínica, ayudar con la agenda, cobrar abonos y anularlos.", noPuede: "Escribir o firmar en la historia clínica, ni anular facturas electrónicas." },
  recepcion: { nombre: "Recepción", puede: "Registrar pacientes, agendar y confirmar citas, cobrar abonos, corregir un monto mal digitado y enviar recordatorios.", noPuede: "Ver la historia clínica, el odontograma ni los documentos clínicos." },
  director: { nombre: "Director/a de clínicas", puede: "Todo lo del administrador, y configurar periodos, clínicas, requisitos, rúbricas y matrículas; ver el avance de todos los estudiantes.", noPuede: "—" },
  tutor: { nombre: "Docente tutor/a", puede: "Atender como odontólogo; aprobar historias y planes, autorizar procedimientos, validarlos con su rúbrica y dar de alta a los pacientes de sus estudiantes.", noPuede: "Configurar la clínica ni manejar usuarios." },
  estudiante: { nombre: "Estudiante", puede: "Atender a sus pacientes: historia clínica, odontograma, plan, notas y documentos; pedir cada aprobación a su tutor y ver su avance.", noPuede: "Marcar procedimientos como realizados sin la validación del tutor, cobrar ni configurar la clínica." },
  laboratorio: { nombre: "Laboratorio dental", puede: "Ver solo las órdenes de trabajo que la clínica le envía (con el nombre del paciente y las fotos del caso), cambiar su estado, chatear y adjuntar enlaces de descarga.", noPuede: "Ver pacientes, historia clínica, agenda, caja ni nada más de la clínica." },
};

/** Colores de marca (el mismo juego del prototipo). */
export const ACENTOS: Readonly<Record<string, { nombre: string; claro: string; suave: string; oscuro: string; suaveOscuro: string }>> = {
  verde: { nombre: "Verde clínico", claro: "#0A6E5C", suave: "#D5EBE5", oscuro: "#3CC4A6", suaveOscuro: "#16392F" },
  azul: { nombre: "Azul", claro: "#1F5FBF", suave: "#DCE7F8", oscuro: "#7AA7FF", suaveOscuro: "#1B2A45" },
  violeta: { nombre: "Violeta", claro: "#6B3FA0", suave: "#EADFF5", oscuro: "#B794F4", suaveOscuro: "#2C2140" },
  vino: { nombre: "Vino", claro: "#9E2B4E", suave: "#F5DDE5", oscuro: "#F08BAE", suaveOscuro: "#3A1C27" },
  grafito: { nombre: "Grafito", claro: "#33475B", suave: "#E1E7EE", oscuro: "#A9BCD0", suaveOscuro: "#212B36" },
  beige: { nombre: "Beige", claro: "#7A6652", suave: "#EEE6DB", oscuro: "#D8C3A6", suaveOscuro: "#2E2820" },
  arena: { nombre: "Arena", claro: "#8C6A3F", suave: "#F3E6CF", oscuro: "#E2BE86", suaveOscuro: "#33291A" },
};

export interface DatosUsuario { nombre: string; email: string; rol: string; registro: string; telefono: string }

export function problemasUsuario(u: DatosUsuario): string[] {
  const p: string[] = [];
  if (u.nombre.trim().length < 3) p.push("Escribe el nombre completo.");
  if (u.nombre.length > 80) p.push("El nombre es demasiado largo.");
  if (!correoValido(u.email.trim())) p.push("Revisa el correo electrónico.");
  if (!(ROLES as readonly string[]).includes(u.rol)) p.push("Elige un rol válido.");
  if (["odontologo", "especialista", "tutor", "director"].includes(u.rol) && !u.registro.trim()) p.push("Los odontólogos, especialistas, tutores y directores necesitan su registro profesional (firma recetas y certificados).");
  if (u.registro.length > 60) p.push("El registro profesional es demasiado largo.");
  if (u.telefono && !celularValido(u.telefono)) p.push("El celular debe ser 09XXXXXXXX o, si es de otro país, empezar con + y el código del país.");
  return p;
}

/**
 * ¿Se puede aplicar este cambio sin dejar la clínica sin administrador ni quitarse uno mismo el acceso?
 * `admins` = administradores activos antes del cambio.
 */
export function problemasCambioUsuario(o: { esUnoMismo: boolean; rolAntes: string; rolDespues: string; activoAntes: boolean; activoDespues: boolean; admins: number }): string[] {
  const p: string[] = [];
  if (o.esUnoMismo && !o.activoDespues) p.push("No puedes desactivar tu propia cuenta.");
  if (o.esUnoMismo && o.rolAntes === "admin" && o.rolDespues !== "admin") p.push("No puedes quitarte el rol de administrador: pídeselo a otro administrador.");
  const dejaDeSerAdmin = o.rolAntes === "admin" && o.activoAntes && (o.rolDespues !== "admin" || !o.activoDespues);
  if (dejaDeSerAdmin && o.admins <= 1) p.push("La clínica debe tener al menos un administrador activo.");
  return p;
}

/** Contadores de numeración que el administrador puede ajustar (al migrar desde otro sistema). */
export const CONTADORES: Readonly<Record<string, string>> = { hc: "Historia clínica", receta: "Receta", recibo: "Recibo de abono", certificado: "Certificado" };

/** El próximo número solo puede subir: así nunca se repite un número ya usado. */
export function problemasNumeracion(actual: number, proximo: number): string | null {
  if (!Number.isInteger(proximo) || proximo < 1 || proximo > 99_999_999) return "El número debe ser un entero positivo.";
  if (proximo <= actual) return `Ya se usó hasta el ${actual}: el próximo debe ser ${actual + 1} o mayor.`;
  return null;
}

/** Imagen de logo: se reconoce por su contenido, no por el nombre del archivo. */
export function tipoImagen(b: Uint8Array): "image/png" | "image/jpeg" | "image/webp" | null {
  if (b.length > 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return "image/png";
  if (b.length > 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
  if (b.length > 12 && String.fromCharCode(...b.subarray(0, 4)) === "RIFF" && String.fromCharCode(...b.subarray(8, 12)) === "WEBP") return "image/webp";
  return null;
}

export const LOGO_MAX_BYTES = 500 * 1024;

/** Ciudad para certificados y documentos, a partir de la dirección ("Av. X y Y, Quito" → "Quito"). */
export function ciudadDeDireccion(direccion: string): string {
  const partes = direccion.split(",").map((x) => x.trim()).filter(Boolean);
  const u = partes.length > 1 ? partes[partes.length - 1] : "";
  return u && !/\d/.test(u) && norm(u).length <= 40 ? u : "";
}

/* ---------- paleta completa a partir del color elegido ---------- */

function hexAHsl(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16), r = (n >> 16) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2, d = max - min;
  if (!d) return [0, 0, l * 100];
  const s = d / (1 - Math.abs(2 * l - 1));
  const h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [(h * 60 + 360) % 360, s * 100, l * 100];
}
function hslAHex(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return "#" + [r, g, b].map((v) => Math.round((v + m) * 255).toString(16).padStart(2, "0")).join("").toUpperCase();
}

/** Contraste WCAG entre dos colores (1 a 21). */
export function contraste(a: string, b: string): number {
  const lum = (hex: string) => {
    const n = parseInt(hex.slice(1), 16);
    const [r, g, bb] = [n >> 16, (n >> 8) & 255, n & 255].map((v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; });
    return 0.2126 * r + 0.7152 * g + 0.0722 * bb;
  };
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}

export type VariablesTema = Record<"--bg" | "--surface" | "--surface-2" | "--ink" | "--muted" | "--line" | "--line-strong" | "--tooth" | "--accent" | "--accent-soft" | "--accent-ink" | "--shadow", string>;

/**
 * Toda la paleta de la app (fondo, tarjetas, líneas, textos y acento) teñida con el color elegido,
 * en modo claro y oscuro. Los colores clínicos (rojo = por realizar, azul = realizado, ámbar = aviso)
 * no cambian: tienen significado en el odontograma y los avisos.
 */
export function paletaDeAcento(clave: string): { claro: VariablesTema; oscuro: VariablesTema } {
  const a = ACENTOS[clave] ?? ACENTOS.verde;
  const [h, sAcento] = hexAHsl(a.claro);
  const k = Math.min(1, Math.max(0.55, sAcento / 75)); // colores apagados (beige, grafito) tiñen menos
  const t = (s: number, l: number) => hslAHex(h, s * k, l);
  const sombra = (hex: string, o1: number, o2: number) => {
    const n = parseInt(hex.slice(1), 16);
    const rgb = `${n >> 16},${(n >> 8) & 255},${n & 255}`;
    return `0 1px 2px rgba(${rgb},${o1}),0 4px 16px rgba(${rgb},${o2})`;
  };
  const tintaClara = t(48, 13);
  return {
    claro: {
      "--bg": t(34, 94), "--surface": t(45, 99.3), "--surface-2": t(30, 89.5), "--ink": tintaClara, "--muted": t(20, 35),
      "--line": t(26, 81), "--line-strong": t(20, 58), "--tooth": t(30, 98.5),
      "--accent": a.claro, "--accent-soft": a.suave, "--accent-ink": "#FFFFFF", "--shadow": sombra(tintaClara, 0.06, 0.06),
    },
    oscuro: {
      "--bg": t(32, 7.5), "--surface": t(30, 11), "--surface-2": t(28, 15), "--ink": t(28, 92), "--muted": t(18, 63),
      "--line": t(24, 21), "--line-strong": t(18, 38), "--tooth": t(24, 18),
      "--accent": a.oscuro, "--accent-soft": a.suaveOscuro, "--accent-ink": hslAHex(h, 60 * k, 8), "--shadow": "0 1px 2px rgba(0,0,0,.3),0 4px 16px rgba(0,0,0,.25)",
    },
  };
}
